import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  Icon,
  Loader,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader
} from '@clientbase/clientbase-library'
import { Box, Stack, Typography, useMediaQuery } from '@mui/material'
import { DataService } from 'api/DataService'
import { NFeParams } from 'models/NFe'
import moment from 'moment'
import * as InvoiceStyles from 'templates/Invoice/components/InvoiceTable/InvoiceTable.styles'

import IconContainer from 'components/IconContainer/IconContainer'

import { formatMoney } from 'utils'
import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

interface ModalLinkNfseToBillingProps {
  billingUuid: string
  billingAmount?: string | number
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const STATUS_LABEL: Record<string, string> = {
  confirmed: 'Emitida',
  issued: 'Programada',
  pending: 'Pendente',
  failed: 'Falhada',
  canceled: 'Cancelada'
}

const normalizeAmount = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const ModalLinkNfseToBilling = ({
  billingUuid,
  billingAmount,
  open,
  onClose,
  onSuccess
}: ModalLinkNfseToBillingProps) => {
  const [loading, setLoading] = useState(false)
  const [nfses, setNfses] = useState<NFeParams[]>([])
  const [linkingUuid, setLinkingUuid] = useState<string | null>(null)
  const isMobile = useMediaQuery('(max-width:600px)', { noSsr: false })

  const billingAmountNumber = useMemo(
    () => normalizeAmount(billingAmount),
    [billingAmount]
  )

  const fetchEligible = useCallback(async () => {
    try {
      setLoading(true)
      const response = await DataService({
        type: 'GET',
        url: `/v1/billings/${billingUuid}/eligible_nfses?per=100`
      })
      if (response.error) throw new Error(response.message)
      setNfses(response.data?.records || [])
    } catch (err) {
      toast.error('Erro ao carregar as notas fiscais elegíveis.')
    } finally {
      setLoading(false)
    }
  }, [billingUuid])

  useEffect(() => {
    if (open) fetchEligible()
  }, [open, fetchEligible])

  const sortedNfses = useMemo(() => {
    const ts = (n: NFeParams) =>
      new Date(n?.nfse_item?.created_at || n?.created_at || 0).getTime()
    return [...nfses].sort((a, b) => ts(b) - ts(a))
  }, [nfses])

  const handleLink = async (nfseUuid: string) => {
    setLinkingUuid(nfseUuid)
    const id = toast.loading('Vinculando nota fiscal...')

    const response = await DataService({
      type: 'POST',
      data: { nfse_uuid: nfseUuid },
      url: `/v1/billings/${billingUuid}/link_nfse`
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    setLinkingUuid(null)

    if (!response.error) {
      onSuccess()
      onClose()
    }
  }

  return (
    <Modal
      open={open}
      handleClose={onClose}
      width="100%"
      maxWidth="720px"
      maxHeight="90vh"
    >
      <ModalBody>
        <ModalHeader onClose={onClose}>
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer icon="description" />
            <Typography variant="mdBold">
              Vincular nota fiscal existente
            </Typography>
          </Stack>
        </ModalHeader>

        <ModalContent>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              py="40px"
              role="status"
              aria-live="polite"
              aria-label="Carregando notas fiscais elegíveis"
            >
              <Loader />
            </Box>
          ) : sortedNfses.length === 0 ? (
            <Box py="40px" textAlign="center">
              <Typography variant="smLight" color="neutral.400">
                Nenhuma nota fiscal avulsa elegível para esta cobrança.
              </Typography>
            </Box>
          ) : (
            <Stack gap={0} mt={1}>
              {sortedNfses.map((nfse) => {
                const amount = normalizeAmount(nfse?.nfse_item?.amount)
                const isSameAmount =
                  billingAmountNumber !== null &&
                  amount !== null &&
                  Math.abs(amount - billingAmountNumber) < 0.005
                const issuedAt = nfse?.nfse_item?.created_at || nfse?.created_at
                const description = nfse?.nfse_item?.description

                return (
                  <Box
                    key={nfse.uuid}
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    gap={2}
                    sx={{
                      py: 1.5,
                      borderBottom: '1px solid',
                      borderBottomColor: 'neutral.700',
                      '&:last-of-type': {
                        borderBottom: 'none'
                      }
                    }}
                  >
                    <Box flex={1} minWidth={0}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                        flexWrap="wrap"
                      >
                        <Typography variant="smBold">
                          Nota{nfse.nfse_number ? ` ${nfse.nfse_number}` : ''}
                        </Typography>
                        {isSameAmount && (
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <Icon
                              icon="checkCircle"
                              width="14px"
                              height="14px"
                              colorSX={theme.palette.primary[100]}
                            />
                            <Typography variant="xsBold" color="primary.100">
                              Mesmo valor
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                      {description && (
                        <Typography
                          variant="smLight"
                          color="neutral.400"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            mt: '2px'
                          }}
                        >
                          {description}
                        </Typography>
                      )}
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={3}
                        mt={'4px'}
                        flexWrap="wrap"
                      >
                        <Box>
                          <Typography
                            variant="smLight"
                            color="neutral.400"
                            component="span"
                          >
                            Valor:{' '}
                          </Typography>
                          <Typography
                            variant="smBold"
                            component="span"
                            color="neutral.200"
                          >
                            {formatMoney(amount ?? 0)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="smLight"
                            color="neutral.400"
                            component="span"
                          >
                            Data:{' '}
                          </Typography>
                          <Typography
                            variant="smBold"
                            component="span"
                            color="neutral.200"
                          >
                            {issuedAt
                              ? moment(issuedAt).format('DD/MM/YYYY')
                              : '-'}
                          </Typography>
                        </Box>
                        <InvoiceStyles.Status status={nfse.status}>
                          {STATUS_LABEL[nfse.status] || nfse.status}
                        </InvoiceStyles.Status>
                      </Stack>
                    </Box>
                    <Button
                      variantButton="outlined"
                      onClick={() => handleLink(nfse.uuid)}
                      disabled={linkingUuid !== null}
                      fullWidth={isMobile}
                    >
                      {linkingUuid === nfse.uuid ? 'Vinculando...' : 'Vincular'}
                    </Button>
                  </Box>
                )
              })}
            </Stack>
          )}
        </ModalContent>
      </ModalBody>
    </Modal>
  )
}

export default ModalLinkNfseToBilling
